import api from './api';
import { queryCache, queryKeys, type FetchQueryOptions } from './queryCache';
import type { ContractorTimelineResponse } from './chatSessionCache';

export interface ChatApiError extends Error {
  status?: number;
  statusCode?: number;
  messageFromApi?: string;
}

export interface ChatMessage {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
  created_at?: string;
  has_image?: boolean;
  image_url?: string | null;
  image_filename?: string | null;
  image_mime_type?: string | null;
  image_size_bytes?: number | null;
}

export interface ChatSession {
  session_id: string;
  created_at: string;
  updated_at: string;
  metadata: Record<string, any>;
  title?: string | null;
  session_title: string;
  escalation_status: string;
}

export interface ChatHistoryResponse {
  success: boolean;
  status_code: number;
  message: string;
  data: {
    messages: ChatMessage[];
    has_more: boolean;
    session: ChatSession;
  };
}

export interface SendAgentMessagePayload {
  user_name: string;
  session_id: string;
  message: string;
  manufacturer_slug?: string;
  assistant_mode: AssistantMode;
  image_base64?: string;
  image_filename?: string;
  agent_context?: string;
  service_titan_selection?: ServiceTitanSelection;
}

export type AssistantMode = 'manuals' | 'parts' | 'ahri' | 'servicetitan';

export interface ServiceTitanConnectionStatus {
  available: boolean;
  authorized: boolean;
  connected: boolean;
  service_titan_connected: boolean;
  requires_reauthorization: boolean;
  missing_scopes: string[];
  status: 'disconnected' | 'authorizing' | 'connected' | 'error' | string;
  mode: 'read-plus-confirmed-equipment-create';
  equipment_read_enabled: boolean;
  equipment_write_enabled: boolean;
  write_tools_enabled: boolean;
  service_titan_environment?: string | null;
  service_titan_tenant_id?: string | null;
  last_verified_at?: string | null;
  message: string;
}

export interface ServiceTitanEquipmentReviewArtifact {
  kind: 'servicetitan_equipment_review';
  title: string;
  status: 'prepared' | 'created' | 'duplicate' | 'invalidated';
  draft_id: string;
  expires_at?: string | null;
  job?: Record<string, any>;
  job_display_label?: string;
  location?: Record<string, any>;
  equipment_draft?: Record<string, any>;
  installation_date?: {
    date: string;
    source: 'technician_supplied' | 'installed_today' | 'confirmed_approximate';
    source_label: string;
  };
  duplicate_check?: {
    exactMatches?: Array<Record<string, any>>;
    potentialMatches?: Array<Record<string, any>>;
    checked?: number;
  };
  can_commit: boolean;
  confirmation_text: string;
  source_photos?: Array<{
    message_id: string;
    file_name: string;
    content_type: string;
    size_bytes: number;
  }>;
  equipment?: Record<string, any>;
  commit_result?: Record<string, any>;
  read_back_verified?: boolean;
  invalidation_reason?: string;
}

export interface ServiceTitanSelection {
  selection_token: string;
  choice_number: number;
}

export interface ServiceTitanChoicePromptArtifact {
  kind: 'servicetitan_choice_prompt';
  title: string;
  prompt: string;
  selection_token: string;
  selection_type: 'customer' | 'location' | 'job' | 'equipment';
  expires_at?: string | null;
  choices: Array<{
    choice_number: number;
    display_label: string;
  }>;
}

export interface PartsModelChoicePromptArtifact {
  kind: 'parts_model_choice_prompt';
  title: string;
  prompt: string;
  selection_type: 'parts_model';
  manufacturer_slug: string;
  expires_at?: string | null;
  choices: Array<{
    choice_id: string;
    label: string;
    description?: string | null;
  }>;
}

export interface ManufacturerOption {
  slug: string;
  displayName: string;
  partsSupported: boolean;
}

export interface AssistantOptionsResponse {
  manufacturers: ManufacturerOption[];
  preferred_oem_slug: string | null;
  supported_modes: Array<{ id: AssistantMode; displayName: string }>;
  service_titan: ServiceTitanConnectionStatus;
}

export interface MessageFeedback {
  rating: 'up' | 'down';
  reasons: string[];
  comment?: string | null;
  updated_at?: string | null;
}

export interface MessageFeedbackPayload {
  rating: 'up' | 'down';
  reasons?: string[];
  comment?: string;
}

export const chatApi = {
  getAssistantOptions: async (): Promise<AssistantOptionsResponse> => {
    const response = await api.get('/chat/assistant-options', {
      params: { surface: 'contractor' },
    });
    return response.data.data;
  },

  updateAssistantPreference: async (preferredOemSlug: string) => {
    const response = await api.patch('/chat/assistant-preferences', {
      preferred_oem_slug: preferredOemSlug,
      surface: 'contractor',
    });
    return response.data.data;
  },

  getServiceTitanStatus: async (): Promise<ServiceTitanConnectionStatus> => {
    const response = await api.get('/servicetitan/status');
    return response.data.data;
  },

  startServiceTitanConnection: async (
    returnPath: string,
    returnTarget: 'web' | 'native',
  ): Promise<{ authorization_url: string; expires_at: string }> => {
    const response = await api.post('/servicetitan/connect', {
      return_path: returnPath,
      return_target: returnTarget,
    });
    return response.data.data;
  },

  verifyServiceTitanConnection: async (): Promise<ServiceTitanConnectionStatus> => {
    const response = await api.post('/servicetitan/verify');
    return response.data.data;
  },

  disconnectServiceTitan: async (): Promise<ServiceTitanConnectionStatus> => {
    const response = await api.post('/servicetitan/disconnect');
    return response.data.data;
  },

  commitServiceTitanEquipment: async (
    sessionId: string,
    artifact: ServiceTitanEquipmentReviewArtifact,
  ): Promise<ServiceTitanEquipmentReviewArtifact> => {
    const response = await api.post('/servicetitan/equipment/commit', {
      session_id: sessionId,
      draft_id: artifact.draft_id,
      confirmation_text: artifact.confirmation_text,
    });
    return response.data.data;
  },

  // Get all sessions for the current user
  getUserSessions: async (options: FetchQueryOptions = {}) =>
    queryCache.fetchQuery(
      queryKeys.chatSessions,
      async () => {
        const response = await api.get('/chat/sessions');
        return response.data;
      },
      { staleTimeMs: 30_000, ...options },
    ),

  updateSessionTitle: async (sessionId: string, title: string | null) => {
    const response = await api.patch(`/chat/session/${sessionId}/title`, { title });
    const nextTitle = response.data?.data?.session?.title ?? title;
    queryCache.setQueryData<any>(queryKeys.chatSessions, (previous) => {
      if (!previous?.data?.sessions) return previous;
      return {
        ...previous,
        data: {
          ...previous.data,
          sessions: previous.data.sessions.map((session: ChatSession) =>
            session.session_id === sessionId ? { ...session, title: nextTitle } : session,
          ),
        },
      };
    });
    const cachedTimeline = queryCache.getQueryData<ContractorTimelineResponse>(
      queryKeys.chatTimeline(sessionId),
    );
    if (cachedTimeline) {
      queryCache.setQueryData<ContractorTimelineResponse>(
        queryKeys.chatTimeline(sessionId),
        {
          ...cachedTimeline,
          data: {
            ...cachedTimeline.data,
            session: { ...cachedTimeline.data.session, title: nextTitle },
          },
        },
      );
    }
    return response.data;
  },

  // Create a new session without sending an initial message.
  // For SubscriptionUser, distributor/contractor/region are optional (defaults used by backend).
  createSession: async (
    distributorName?: string,
    contractorName?: string,
    region?: string
  ) => {
    try {
      const body: Record<string, string> = {};
      if (distributorName != null && distributorName !== '') body.distributor_name = distributorName;
      if (contractorName != null && contractorName !== '') body.contractor_name = contractorName;
      if (region != null && region !== '') body.region = region;
      console.log('[chatApi] Creating new session request', {
        hasDistributorName: Boolean(body.distributor_name),
        hasContractorName: Boolean(body.contractor_name),
        hasRegion: Boolean(body.region),
      });
      const response = await api.post('/chat/create-session', body);
      queryCache.invalidateQueries(queryKeys.chatSessions);
      console.log('[chatApi] Session created successfully');
      return response.data;
    } catch (error: any) {
      console.error('[chatApi] Failed to create session', {
        status: error?.response?.status,
      });
      if (error.response && error.response.data) {
        const msg = error.response.data.message || 'Failed to create session';
        const code = error.response.data.code;
        const err = new Error(msg) as Error & { code?: string };
        if (code) err.code = code;
        throw err;
      }
      throw error;
    }
  },

  // Get chat history for a specific session
  getChatHistory: async (sessionId: string, page: number = 1, perPage: number = 100) => {
    try {
      console.log('[chatApi] Fetching chat history', {
        page,
        perPage,
      });
      const response = await api.get<ChatHistoryResponse>(`/chat/history/${sessionId}`, {
        params: { page, per_page: perPage },
        timeout: 30000, // 30 second timeout
      });
      console.log('[chatApi] Chat history retrieved');
      return response.data;
    } catch (error: any) {
      console.error('[chatApi] Failed to fetch chat history', {
        status: error?.response?.status,
      });
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'Failed to get chat history');
      }
      throw error;
    }
  },

  getContractorTimeline: async (
    sessionId: string,
    options: FetchQueryOptions = {},
  ): Promise<ContractorTimelineResponse> =>
    queryCache.fetchQuery(
      queryKeys.chatTimeline(sessionId),
      async () => {
        const response = await api.get<ContractorTimelineResponse>(
          `/chat/session/${sessionId}/contractor_timeline`,
        );
        return response.data;
      },
      { staleTimeMs: 15_000, ...options },
    ),

  // Check if a session exists
  checkSession: async (sessionId: string) => {
    try {
      const response = await api.get(`/chat/history/${sessionId}`, {
        params: { limit: 1 },
        timeout: 10000,
      });
      return response.data.success;
    } catch (error) {
      return false;
    }
  },

  // Send a message in a chat session or create a new session
  sendMessage: async (
    message: string,
    sessionId?: string,
    distributorName?: string,
    contractorName?: string,
    region?: string,
    isInitializing: boolean = false,
  ) => {
    const payload = {
      message: isInitializing ? '' : message, // Empty message when initializing
      session_id: sessionId,
      distributor_name: distributorName,
      contractor_name: contractorName,
      region: region,
    };

    const response = await api.post('/chat/message', payload, {
      timeout: 60000, // 60 second timeout for message responses
    });
    return response.data;
  },

  // Send one scoped request to the backend-owned SmartDocs assistant.
  sendMessageToAgent: async (
    userName: string,
    sessionId: string,
    message: string,
    manufacturerSlug: string,
    assistantMode: AssistantMode,
    imageBase64?: string,
    imageFilename?: string,
    agentContext?: string,
    serviceTitanSelection?: ServiceTitanSelection,
  ) => {
    try {
      console.log('[chatApi] Sending message to the SmartDocs assistant', {
        hasMessage: Boolean(message?.trim()),
        hasImage: Boolean(imageBase64),
        manufacturerSlug,
        assistantMode,
        hasAgentContext: Boolean(agentContext?.trim()),
      });
      const payload: SendAgentMessagePayload = {
        user_name: userName,
        session_id: sessionId,
        message: message,
        ...(manufacturerSlug ? { manufacturer_slug: manufacturerSlug } : {}),
        assistant_mode: assistantMode,
        image_base64: imageBase64,
        image_filename: imageFilename,
      };
      if (agentContext?.trim()) {
        payload.agent_context = agentContext;
      }
      if (serviceTitanSelection) {
        payload.service_titan_selection = serviceTitanSelection;
      }
      // This request only starts/joins the agent turn; live progress and text stream over WebSocket.
      // Let the backend finish long manual/PDF investigations instead of surfacing a false client timeout.
      const response = await api.post('/chat/send-message', payload, {
        timeout: 0,
      });
      queryCache.invalidateQueries(queryKeys.chatSessions);
      queryCache.invalidateQueries(queryKeys.chatTimeline(payload.session_id));
      console.log('[chatApi] SmartDocs assistant request accepted');
      return response.data;
    } catch (error: any) {
      console.error('[chatApi] Failed to send message to the SmartDocs assistant', {
        status: error?.response?.status,
      });
      const apiMessage = error.response?.data?.message;
      const status = error.response?.status;
      const statusCode = error.response?.data?.status_code;
      const err = new Error(apiMessage || 'Failed to send message to SmartDocs') as ChatApiError;
      err.status = status;
      err.statusCode = statusCode;
      err.messageFromApi = apiMessage;
      throw err;
    }
  },

  interruptMessageToAgent: async (
    userName: string,
    sessionId: string,
    message: string,
    manufacturerSlug: string,
    assistantMode: AssistantMode,
    imageBase64?: string,
    imageFilename?: string
  ) => {
    try {
      const payload: SendAgentMessagePayload = {
        user_name: userName,
        session_id: sessionId,
        message,
        ...(manufacturerSlug ? { manufacturer_slug: manufacturerSlug } : {}),
        assistant_mode: assistantMode,
        image_base64: imageBase64,
        image_filename: imageFilename,
      };
      const response = await api.post('/chat/interrupt-message', payload, {
        timeout: 0,
      });
      return response.data;
    } catch (error: any) {
      const apiMessage = error.response?.data?.message;
      const status = error.response?.status;
      const statusCode = error.response?.data?.status_code;
      const err = new Error(apiMessage || 'Failed to queue follow-up message') as ChatApiError;
      err.status = status;
      err.statusCode = statusCode;
      err.messageFromApi = apiMessage;
      throw err;
    }
  },

  stopMessageToAgent: async (
    userName: string,
    sessionId: string
  ) => {
    try {
      const response = await api.post('/chat/stop-message', {
        user_name: userName,
        session_id: sessionId,
      }, {
        timeout: 15000,
      });
      return response.data;
    } catch (error: any) {
      const apiMessage = error.response?.data?.message;
      const status = error.response?.status;
      const statusCode = error.response?.data?.status_code;
      const err = new Error(apiMessage || 'Failed to stop agent run') as ChatApiError;
      err.status = status;
      err.statusCode = statusCode;
      err.messageFromApi = apiMessage;
      throw err;
    }
  },

  submitMessageFeedback: async (
    sessionId: string,
    messageId: string,
    payload: MessageFeedbackPayload
  ) => {
    try {
      const response = await api.post(
        `/chat/session/${sessionId}/messages/${messageId}/feedback`,
        payload,
      );
      const feedback = response.data?.data?.feedback ?? {
        rating: payload.rating,
        reasons: payload.reasons ?? [],
        comment: payload.comment ?? null,
      };
      const cachedTimeline = queryCache.getQueryData<ContractorTimelineResponse>(
        queryKeys.chatTimeline(sessionId),
      );
      if (cachedTimeline) {
        queryCache.setQueryData<ContractorTimelineResponse>(
          queryKeys.chatTimeline(sessionId),
          {
            ...cachedTimeline,
            data: {
              ...cachedTimeline.data,
              timeline: cachedTimeline.data.timeline.map((message) =>
                message.id === messageId ? { ...message, feedback } : message,
              ),
            },
          },
        );
      }
      return response.data;
    } catch (error: any) {
      const apiMessage = error.response?.data?.message;
      const status = error.response?.status;
      const statusCode = error.response?.data?.status_code;
      const err = new Error(apiMessage || 'Failed to submit message feedback') as ChatApiError;
      err.status = status;
      err.statusCode = statusCode;
      err.messageFromApi = apiMessage;
      throw err;
    }
  },
};
